import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Network, AlertTriangle, CheckCircle2, Lock } from "lucide-react";
import { DefaultReferralCodeCard } from "./Distributors";

/** Master-only page for configuring the Auto-Pull referral allocator.
 *  Strategy: Matrix Fill — when a signup arrives without a referral code and
 *  auto-pull is enabled, the system performs BFS from the default-code owner
 *  and places the newcomer under the first eligible parent with fewer than
 *  `matrix_width` direct referrals. Eligible = joining-fee paid + KYC verified. */
export default function ReferralSettings() {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";

  const [s, setS] = useState(null);
  const [draft, setDraft] = useState({ auto_pull_enabled: false, strategy: "matrix_fill", matrix_width: 3 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/settings/referral`, { headers: getAuthHeaders() })
      .then(r => {
        setS(r.data);
        setDraft({
          auto_pull_enabled: !!r.data.auto_pull_enabled,
          strategy: r.data.strategy || "matrix_fill",
          matrix_width: r.data.matrix_width || 3,
        });
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/admin/settings/referral`,
        draft,
        { headers: getAuthHeaders() }
      );
      setS({ ...s, ...r.data });
      toast.success(draft.auto_pull_enabled ? "Auto-Pull enabled" : "Auto-Pull disabled");
    } catch (err) {
      const d = err.response?.data?.detail || "Failed to save";
      setError(typeof d === "string" ? d : "Failed to save");
      toast.error(typeof d === "string" ? d : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const dirty = !s || draft.auto_pull_enabled !== !!s.auto_pull_enabled
    || draft.strategy !== s.strategy
    || draft.matrix_width !== s.matrix_width;

  return (
    <div className="space-y-6" data-testid="referral-settings-section">
      {/* Step 1: Default Referral Code (anchors the matrix root) */}
      <DefaultReferralCodeCard />

      {/* Step 2: Auto-Pull configuration */}
      <div className="p-5 rounded-xl border border-white/10 bg-[#13131A]/60" data-testid="auto-pull-card">
        <div className="flex items-center gap-2 mb-1">
          <Network size={14} className="text-[#7DD3FC]" />
          <h3 className="text-sm font-bold text-white tracking-tight">Auto-Pull Referral Allocation</h3>
        </div>
        <p className="text-[11px] text-zinc-500 mb-5 max-w-2xl">
          When ON, new signups without a referral code are placed under the next eligible affiliate using the
          chosen strategy. When OFF, signups without a code are credited to the Default Referral Code owner.
        </p>

        {!isMaster && (
          <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-200 flex items-center gap-2">
            <Lock size={12} /> Read-only — Master Admin can edit these settings.
          </div>
        )}

        {/* Toggle */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-950/60 border border-white/5 mb-4">
          <div>
            <p className="text-xs font-semibold text-white">Auto-Pull Mode</p>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              {draft.auto_pull_enabled
                ? "Distribute no-code signups across the network."
                : "Send no-code signups to the Default Referral Code owner."}
            </p>
          </div>
          <button
            data-testid="auto-pull-toggle"
            type="button"
            disabled={!isMaster}
            onClick={() => setDraft(d => ({ ...d, auto_pull_enabled: !d.auto_pull_enabled }))}
            className={`relative w-12 h-6 rounded-full transition-colors ${draft.auto_pull_enabled ? "bg-[#7DD3FC]" : "bg-zinc-700"} disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <span className={`absolute top-0.5 ${draft.auto_pull_enabled ? "left-6" : "left-0.5"} w-5 h-5 rounded-full bg-white transition-all`} />
          </button>
        </div>

        {/* Strategy */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Strategy</label>
            <select
              data-testid="auto-pull-strategy"
              disabled={!isMaster}
              value={draft.strategy}
              onChange={(e) => setDraft(d => ({ ...d, strategy: e.target.value }))}
              className="mt-1 w-full px-3 py-2 rounded-md bg-zinc-950 border border-zinc-800 text-white text-sm outline-none focus:border-[#7DD3FC]/50 disabled:opacity-40"
            >
              <option value="matrix_fill">Matrix Fill (BFS, top → bottom, left → right)</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Matrix Width</label>
            <input
              data-testid="auto-pull-matrix-width"
              type="number"
              min={1}
              max={50}
              disabled={!isMaster}
              value={draft.matrix_width}
              onChange={(e) => setDraft(d => ({ ...d, matrix_width: Math.max(1, Math.min(50, parseInt(e.target.value) || 1)) }))}
              className="mt-1 w-full px-3 py-2 rounded-md bg-zinc-950 border border-zinc-800 text-white text-sm font-mono outline-none focus:border-[#7DD3FC]/50 disabled:opacity-40"
            />
            <p className="text-[10px] text-zinc-500 mt-1">Max direct referrals per matrix slot before spillover.</p>
          </div>
        </div>

        {/* Matrix root preview */}
        {s?.matrix_root_owner ? (
          <div className="p-3 rounded-lg bg-zinc-950/60 border border-white/5 mb-4">
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Matrix Root</p>
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className="text-white font-semibold">{s.matrix_root_owner.name || "—"}</span>
              <span className="font-mono text-[#7DD3FC]">{s.matrix_root_owner.display_id || s.matrix_root_owner.user_id}</span>
              <span className="text-zinc-500">·</span>
              <span className="font-mono text-zinc-400">{s.default_referral_code}</span>
              <span className="text-zinc-500">·</span>
              <span className="text-zinc-300">{s.matrix_root_owner.direct_referrals} direct referrals</span>
            </div>
          </div>
        ) : (
          <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-[11px] text-rose-200 flex items-center gap-2 mb-4" data-testid="no-root-warning">
            <AlertTriangle size={12} /> No matrix root — set a Default Referral Code above first.
          </div>
        )}

        {error && (
          <div className="mb-3 p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-[11px] text-rose-300" data-testid="referral-settings-error">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] text-zinc-500">
            {s?.auto_pull_enabled ? (
              <span className="inline-flex items-center gap-1 text-emerald-400"><CheckCircle2 size={11} /> Auto-Pull is currently active</span>
            ) : (
              <span>Auto-Pull is off — falls back to Default Referral Code.</span>
            )}
          </div>
          <button
            data-testid="referral-settings-save"
            type="button"
            onClick={save}
            disabled={!isMaster || saving || !dirty}
            className="px-4 py-2 rounded-md bg-[#7DD3FC] hover:bg-[#BAE6FD] text-[#0A0A0C] text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </div>

      {/* Eligibility explainer */}
      <div className="p-4 rounded-xl border border-white/10 bg-[#13131A]/40 text-[11px] text-zinc-400 leading-relaxed">
        <p className="text-zinc-300 font-semibold mb-1.5">How Matrix Fill works</p>
        <ul className="list-disc pl-4 space-y-1">
          <li>Starts BFS from the Default Referral Code owner.</li>
          <li>Skips any user with <span className="text-white font-semibold">≥ Matrix Width</span> direct referrals and enqueues their children.</li>
          <li>A user must be <span className="text-white font-semibold">paid (₹2,999 joining fee)</span> AND <span className="text-white font-semibold">KYC verified</span> to receive a new referral.</li>
          <li>If no slot is found, the signup falls back to <span className="text-white font-semibold">admin_default</span>.</li>
        </ul>
      </div>
    </div>
  );
}
